<template>
  <div class="relative w-full space-y-1">
    <Menu as="div" class="">
      <Tooltip
        class="justify-start w-full"
        :tooltip-class="!isSidebarCollapsed ? 'hidden' : ''"
        :description="$t(`common.${menu.label}`)"
        width="w-max"
        placement="right"
      >
        <MenuButton
          ref="dropdownButtonRef"
          class="group flex items-center gap-3 p-2 rounded-lg w-full hover:bg-gray-100 font-medium"
          :class="[isSidebarCollapsed && 'justify-center']"
          @click="
            !isSidebarCollapsed ? childCollapse.toggle() : (isOpen = !isOpen)
          "
        >
          <FontAwesomeIcon v-if="menu.icon" :icon="menu.icon" class="w-4" />
          <span
            v-if="!isSidebarCollapsed"
            class="whitespace-nowrap text-sm"
            v-text="$t(`common.${menu.label}`)"
          />

          <FontAwesomeIcon
            v-if="!isSidebarCollapsed"
            :icon="['fas', !childCollapse.isOpen() ? 'angle-down' : 'angle-up']"
            class="ml-auto w-5"
          />
        </MenuButton>
      </Tooltip>

      <template v-if="!isSidebarCollapsed">
        <ul v-if="childCollapse.isOpen()" class="flex-1 flex flex-col">
          <template
            v-for="(childMenu, index) in getActiveChildMenuElements(menu)"
            :key="childMenu.label"
          >
            <!-- A group's name above its pages while more than one group shows (Inhalte). -->
            <li
              v-if="groupHeading(menu, index)"
              class="pt-3 pb-1 pl-4 text-[11px] font-bold tracking-[0.08em] text-gray-500 uppercase"
              :data-test="`menu-group-${childMenu.group}`"
              v-text="$t(groupHeading(menu, index))"
            />
            <li
              class="relative flex h-full items-center"
              :class="childMenu.coming_soon && 'pointer-events-none'"
            >
              <Link
                v-if="!menu.childSubmenu"
                class="group relative flex items-center gap-2 py-2 pr-2 pl-4 rounded-lg w-full hover:bg-gray-100 font-medium"
                :class="[
                  isMenuChildActive($page, childMenu)
                    ? 'text-primary bg-gray-100'
                    : '',
                ]"
                :href="
                  useRoute(childMenu.route, {
                    event: event?.id,
                    ...(childMenu.extraParams || {}),
                    ...(childMenu.entryParams || {}),
                  })
                "
              >
                <template v-if="!isSidebarCollapsed && childMenu.showIcon">
                  <FontAwesomeIcon :icon="childMenu.icon" class="w-4" />
                </template>
                <span
                  v-if="!isSidebarCollapsed"
                  class="whitespace-nowrap text-sm"
                  :class="childMenu.coming_soon && 'opacity-50'"
                  v-text="$t(`common.${childMenu.label}`)"
                />
                <span
                  v-if="!isSidebarCollapsed && childMenu.coming_soon"
                  class="whitespace-nowrap text-[10px] uppercase text-white bg-purple-300 rounded-lg px-1.5 py-0.5 text-center w-max"
                  v-text="$t('common.coming_soon')"
                />
              </Link>
            </li>
          </template>
        </ul>
      </template>

      <template v-if="isSidebarCollapsed">
        <MenuItems
          ref="dropdownItemsRef"
          as="div"
          class="fixed bg-white rounded-lg p-1 shadow-md z-12 w-full max-w-56"
          :style="{
            position: 'fixed',
            top: `${dropdownPosition.top}px`,
            left: `${dropdownPosition.left}px`,
          }"
        >
          <ul class="flex flex-col gap-1">
            <li class="p-2 text-sm font-medium border-b border-gray-100">
              {{ $t(`common.${menu.label}`) }}
            </li>
            <template
              v-for="(childMenu, index) in getActiveChildMenuElements(menu)"
              :key="childMenu.label"
            >
              <li
                v-if="groupHeading(menu, index)"
                class="px-2 pt-2 text-[11px] font-bold tracking-[0.08em] text-gray-500 uppercase"
                v-text="$t(groupHeading(menu, index))"
              />
              <MenuItem
                as="a"
                class="rounded-md w-full p-2 whitespace-nowrap text-sm hover:text-primary"
                :class="isMenuChildActive($page, childMenu) && 'text-primary'"
                :href="
                  useRoute(childMenu.route, {
                    event: event?.id,
                    ...(childMenu.extraParams || {}),
                    ...(childMenu.entryParams || {}),
                  })
                "
              >
                {{ $t(childMenu.labelKey ?? `common.${childMenu.label}`) }}
              </MenuItem>
            </template>
          </ul>
        </MenuItems>
      </template>
    </Menu>
  </div>
</template>

<script setup>
import {
  ref,
  inject,
  computed,
  nextTick,
  watch,
  onMounted,
  onBeforeUnmount,
} from 'vue'
import { Link, usePage } from '@inertiajs/vue3'
import { useRoute } from '@/composables/route'
import { usePermission } from '@/composables/permission'
import { useFeature } from '@/composables/feature.js'
import {
  childGroupHeading,
  isMenuChildActive,
  useMenuChildCollapse,
} from '@/composables/menu'
import { allowsMenuEntry } from '@/composables/subscription'
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/vue'
import Tooltip from '@/Components/common/Tooltip.vue'

const props = defineProps({
  menu: {
    type: Object,
    required: true,
  },
})

const isSidebarCollapsed = inject('isSidebarCollapsed')
const childCollapse = useMenuChildCollapse(props.menu.label)

const event = computed(() => usePage().props.event)

const dropdownButtonRef = ref(null)
const dropdownItemsRef = ref(null)
const dropdownPosition = ref({ top: 0, left: 0 })
const dropUp = ref(false)
const isOpen = ref(false)

const getActiveChildMenuElements = (menu) => {
  return menu.child.filter((child) => {
    let hasPermission = child.permission
      ? usePermission(child.permission)
      : true
    let itemHasSubscription = allowsMenuEntry(child)
    let featureEnabled = child.anyFeature?.length
      ? child.anyFeature.some((feature) => useFeature(feature))
      : child.feature?.length
        ? useFeature(child?.feature)
        : true
    let constantEnabled = child.constantFlag
      ? usePage().props._constants?.[child.constantFlag] === true
      : true

    return (
      hasPermission && itemHasSubscription && featureEnabled && constantEnabled
    )
  })
}

const groupHeading = (menu, index) =>
  childGroupHeading(getActiveChildMenuElements(menu), index)

const positionDropdown = async () => {
  await nextTick()

  const button = dropdownButtonRef.value?.$el
  const items = dropdownItemsRef.value?.$el
  if (!button || !items) return

  const buttonRect = button.getBoundingClientRect()
  const menuHeight = items.offsetHeight
  const spaceBelow = window.innerHeight - buttonRect.bottom

  dropUp.value = spaceBelow < menuHeight

  dropdownPosition.value = dropUp.value
    ? { top: buttonRect.top - menuHeight - 8, left: buttonRect.left }
    : { top: buttonRect.bottom + 8, left: buttonRect.left }
}

watch(isOpen, (open) => {
  if (open) positionDropdown()
})

onMounted(() => {
  window.addEventListener('scroll', positionDropdown, true)
  window.addEventListener('resize', positionDropdown)
})

onBeforeUnmount(() => {
  window.removeEventListener('scroll', positionDropdown, true)
  window.removeEventListener('resize', positionDropdown)
})
</script>
